import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import {
    UploadPlaylistAsset as BLUploadPlaylistAsset,
    UpdateSettings as BLUpdateSettings
} from '../../../../utilities/BL/Admin/Overlays/Playlist';

/**
 * Resuelve la URL absoluta para previsualizar los recursos multimedia cargados.
 * 
 * @param {string} mediaPath - URL relativa o absoluta del recurso.
 * @returns {string} URL formateada para visualización.
 */
export const getFullAssetUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/')
        .replace('/api/', '/')
        .replace('/api', '/');
    const cleanBase = apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`;
    return `${cleanBase}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
};

/**
 * Componente Dropzone Reutilizable para la carga de imágenes con arrastrar y soltar.
 */
const PlaylistAssetDropzone = ({
    label,
    fieldKey,
    value,
    aspectHint = '',
    helpText = '',
    isUploading = false,
    onUpload = () => {},
    onRemove = () => {}
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputId = `file-input-${fieldKey}`;

    const handleFileSelected = (file) => {
        if (!file) return;
        onUpload(file, fieldKey);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelected(e.dataTransfer.files[0]);
        }
    };

    const handleNativeChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFileSelected(e.target.files[0]);
            e.target.value = '';
        }
    };

    return (
        <div className="PlaylistModal__FormGroup">
            <label className="PlaylistModal__Label">
                {label}
                {aspectHint && <span className="PlaylistModal__Hint">{aspectHint}</span>}
            </label>

            <div
                className={`PlaylistModal__Dropzone ${isDragOver ? 'PlaylistModal__Dropzone--DragOver' : ''} ${isUploading ? 'PlaylistModal__Dropzone--Uploading' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => {
                    if (!isUploading) {
                        const inputEl = document.getElementById(fileInputId);
                        if (inputEl) inputEl.click();
                    }
                }}
            >
                <input
                    id={fileInputId}
                    type="file"
                    className="d-none"
                    accept="image/png, image/jpeg, image/webp, image/svg+xml, image/gif"
                    onChange={handleNativeChange}
                    disabled={isUploading}
                />

                {isUploading ? (
                    <div className="d-flex flex-column align-items-center py-3">
                        <span className="PlaylistModal__Spinner mb-2"></span>
                        <span className="small text-cyan fw-bold">Subiendo y optimizando imagen...</span>
                    </div>
                ) : value ? (
                    <div
                        className="PlaylistModal__PreviewBox"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={getFullAssetUrl(value)}
                            alt={label}
                            className="PlaylistModal__PreviewImage"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                        <div className="PlaylistModal__PreviewActions">
                            <span className="PlaylistModal__PreviewFilename" title={value}>
                                {value.split('/').pop()}
                            </span>
                            <button
                                type="button"
                                className="PlaylistModal__Btn PlaylistModal__Btn--Secondary PlaylistModal__Btn--Small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const inputEl = document.getElementById(fileInputId);
                                    if (inputEl) inputEl.click();
                                }}
                                title="Reemplazar archivo"
                            >
                                <i className="bi bi-arrow-repeat"></i> Cambiar
                            </button>
                            <button
                                type="button"
                                className="PlaylistModal__Btn PlaylistModal__Btn--Secondary PlaylistModal__Btn--Small text-danger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemove(fieldKey);
                                }}
                                title="Eliminar imagen"
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up PlaylistModal__DropzoneIcon"></i>
                        <p className="PlaylistModal__DropzoneText">
                            Arrastra aquí tu imagen o haz <strong>clic para explorar</strong>
                        </p>
                        {helpText && (
                            <span className="PlaylistModal__DropzoneBadge">{helpText}</span>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

PlaylistAssetDropzone.propTypes = {
    label: PropTypes.string.isRequired,
    fieldKey: PropTypes.string.isRequired,
    value: PropTypes.string,
    aspectHint: PropTypes.string,
    helpText: PropTypes.string,
    isUploading: PropTypes.bool,
    onUpload: PropTypes.func,
    onRemove: PropTypes.func
};

/**
 * MODAL DE GESTIÓN DE FONDOS Y TEMAS VISUALES [HU-07]
 * 
 * Gestiona los 4 recursos gráficos de Arma la Playlist:
 * 1. Fondo Overlay 1536x180 px
 * 2. Fondo Overlay 1920x180 px
 * 3. Fondo Pantalla Gigante 1920x1080 px
 * 4. Logotipo Central de Códigos QR 512x512 px
 * 
 * @component
 * @param {Object} props
 * @param {boolean} [props.isOpen=true] - Visibilidad del modal.
 * @param {Object|null} props.settings - Configuración visual actual.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} [props.onSuccess] - Callback al guardar o subir cambios.
 * @param {string|null} [props.roomId=null] - Identificador GUID de la sala activa.
 */
export const PlaylistThemeModal = ({
    isOpen = true,
    settings = null,
    onClose = () => {},
    onSuccess = () => {},
    roomId = null
}) => {
    const { showMessage } = useFloatMessage();

    // Estado local de los 4 assets gráficos
    const [assetsState, setAssetsState] = useState({
        overlayBackground1536Url: '',
        overlayBackground1920Url: '',
        screenBackgroundUrl: '',
        qrCenterLogoUrl: ''
    });

    const [uploadingKey, setUploadingKey] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Cargar URLs existentes
    useEffect(() => {
        if (settings) {
            setAssetsState({
                overlayBackground1536Url: settings.overlayBackground1536Url || '',
                overlayBackground1920Url: settings.overlayBackground1920Url || '',
                screenBackgroundUrl: settings.screenBackgroundUrl || '',
                qrCenterLogoUrl: settings.qrCenterLogoUrl || ''
            });
        }
    }, [settings]);

    // Manejador de teclado para cerrar con ESC
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && !uploadingKey && !isSaving) {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, uploadingKey, isSaving, onClose]);

    if (!isOpen) return null;

    /**
     * Sube un archivo a la API mediante BL y actualiza el estado.
     */
    const handleUploadFile = async (file, fieldKey) => {
        if (!file) return;

        // Validaciones básicas de tipo y tamaño (máximo 10MB)
        if (file.size > 10 * 1024 * 1024) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'El archivo excede el tamaño máximo permitido de 10 MB.'
            });
            return;
        }

        setUploadingKey(fieldKey);
        try {
            // Mapeo seguro de la clave de formulario al tipo de asset esperado por el backend
            const assetTypeMap = {
                overlayBackground1536Url: 'overlay1536',
                overlayBackground1920Url: 'overlay1920',
                screenBackgroundUrl: 'screen',
                qrCenterLogoUrl: 'qrLogo'
            };
            const mappedAssetType = assetTypeMap[fieldKey] || 'screen';

            const formData = new FormData();
            formData.append('file', file);
            formData.append('assetType', mappedAssetType);

            const uploadRes = await BLUploadPlaylistAsset(formData, roomId);

            if (uploadRes.success && uploadRes.url) {
                const newAssets = {
                    ...assetsState,
                    [fieldKey]: uploadRes.url
                };
                setAssetsState(newAssets);

                // Auto-guardar en settings vía BL
                const currentRoomId = roomId || (settings && settings.roomId) || null;
                const updateRes = await BLUpdateSettings({
                    roomId: currentRoomId,
                    ...settings,
                    ...newAssets
                });

                if (updateRes.success) {
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: '🎨 Recurso subido y actualizado exitosamente.'
                    });
                    if (typeof onSuccess === 'function') {
                        onSuccess(updateRes.data || { ...settings, ...newAssets });
                    }
                } else {
                    showMessage({
                        type: MESSAGE_TYPE.WARNING,
                        text: 'Recurso subido pero no se pudo sincronizar la configuración.'
                    });
                }
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: uploadRes.message || 'Error al subir el archivo multimedia.'
                });
            }
        } catch (error) {
            console.error('[PlaylistThemeModal] Error al subir asset:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red durante la subida del recurso.'
            });
        } finally {
            setUploadingKey(null);
        }
    };

    /**
     * Remueve la URL de un asset y persiste el cambio.
     */
    const handleRemoveAsset = async (fieldKey) => {
        const newAssets = {
            ...assetsState,
            [fieldKey]: ''
        };
        setAssetsState(newAssets);

        try {
            const currentRoomId = roomId || (settings && settings.roomId) || null;
            const updateRes = await BLUpdateSettings({
                roomId: currentRoomId,
                ...settings,
                ...newAssets
            });

            if (updateRes.success) {
                showMessage({
                    type: MESSAGE_TYPE.INFO,
                    text: 'Recurso removido exitosamente.'
                });
                if (typeof onSuccess === 'function') {
                    onSuccess(updateRes.data || { ...settings, ...newAssets });
                }
            }
        } catch (error) {
            console.error('[PlaylistThemeModal] Error al remover asset:', error);
        }
    };

    /**
     * Guarda la totalidad de los cambios en los assets.
     */
    const handleSaveAll = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            const currentRoomId = roomId || (settings && settings.roomId) || null;
            const updateRes = await BLUpdateSettings({
                roomId: currentRoomId,
                ...settings,
                ...assetsState
            });

            if (updateRes.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '🎨 Tema y fondos de Arma la Playlist guardados exitosamente.'
                });
                if (typeof onSuccess === 'function') {
                    onSuccess(updateRes.data || { ...settings, ...assetsState });
                }
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: updateRes.message || 'Error al guardar la configuración del tema.'
                });
            }
        } catch (error) {
            console.error('[PlaylistThemeModal] Error al persistir tema:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado al guardar el tema.'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="PlaylistModal">
            {/* Backdrop */}
            <div
                className="PlaylistModal__Backdrop"
                onClick={() => !uploadingKey && !isSaving && onClose()}
                aria-hidden="true"
            />

            {/* Contenedor del Modal */}
            <div className="PlaylistModal__Container PlaylistModal__Container--ExtraLarge" role="dialog" aria-modal="true">
                {/* Encabezado */}
                <div className="PlaylistModal__Header">
                    <div className="PlaylistModal__HeaderInfo">
                        <span className="PlaylistModal__HeaderIcon">🎨</span>
                        <div>
                            <h2 className="PlaylistModal__Title">Tema y Fondos - Arma la Playlist</h2>
                            <span className="PlaylistModal__Subtitle">
                                Personaliza los fondos para cintillos de transmisión (1536 y 1920), pantalla gigante y logotipo de QR
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="PlaylistModal__CloseBtn"
                        onClick={onClose}
                        disabled={Boolean(uploadingKey) || isSaving}
                        title="Cerrar modal (ESC)"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {/* Formulario / Cuerpo */}
                <form onSubmit={handleSaveAll} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    <div className="PlaylistModal__Body">
                        <div className="PlaylistModal__Grid">
                            {/* ZONA 1: Fondo Overlay 1536x180 px */}
                            <PlaylistAssetDropzone
                                label="1. Fondo Overlay Banner (1536x180 px)"
                                fieldKey="overlayBackground1536Url"
                                value={assetsState.overlayBackground1536Url}
                                aspectHint="1536 x 180 px"
                                helpText="Formato: PNG, JPG o WebP transparente o con arte institucional"
                                isUploading={uploadingKey === 'overlayBackground1536Url'}
                                onUpload={handleUploadFile}
                                onRemove={handleRemoveAsset}
                            />

                            {/* ZONA 2: Fondo Overlay 1920x180 px */}
                            <PlaylistAssetDropzone
                                label="2. Fondo Overlay Full HD (1920x180 px)"
                                fieldKey="overlayBackground1920Url"
                                value={assetsState.overlayBackground1920Url}
                                aspectHint="1920 x 180 px"
                                helpText="Formato: PNG, JPG o WebP panorámico para pantallas de cabecera"
                                isUploading={uploadingKey === 'overlayBackground1920Url'}
                                onUpload={handleUploadFile}
                                onRemove={handleRemoveAsset}
                            />

                            {/* ZONA 3: Fondo Pantalla Gigante 1920x1080 px */}
                            <PlaylistAssetDropzone
                                label="3. Fondo Pantalla Gigante de Estadio (1920x1080 px)"
                                fieldKey="screenBackgroundUrl"
                                value={assetsState.screenBackgroundUrl}
                                aspectHint="1920 x 1080 px (16:9)"
                                helpText="Fondo principal para las pantallas monumentales durante el evento"
                                isUploading={uploadingKey === 'screenBackgroundUrl'}
                                onUpload={handleUploadFile}
                                onRemove={handleRemoveAsset}
                            />

                            {/* ZONA 4: Logotipo Central de Códigos QR 512x512 px */}
                            <PlaylistAssetDropzone
                                label="4. Logotipo Central de Códigos QR (512x512 px)"
                                fieldKey="qrCenterLogoUrl"
                                value={assetsState.qrCenterLogoUrl}
                                aspectHint="512 x 512 px (1:1)"
                                helpText="Emblema del club o patrocinador insertado en el centro del código QR"
                                isUploading={uploadingKey === 'qrCenterLogoUrl'}
                                onUpload={handleUploadFile}
                                onRemove={handleRemoveAsset}
                            />
                        </div>
                    </div>

                    {/* Pie del Modal */}
                    <div className="PlaylistModal__Footer">
                        <button
                            type="button"
                            className="PlaylistModal__Btn PlaylistModal__Btn--Secondary"
                            onClick={onClose}
                            disabled={Boolean(uploadingKey) || isSaving}
                        >
                            Cerrar
                        </button>
                        <button
                            type="submit"
                            className="PlaylistModal__Btn PlaylistModal__Btn--Cyan"
                            disabled={Boolean(uploadingKey) || isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <span className="PlaylistModal__Spinner"></span>
                                    <span>Guardando Cambios...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-all"></i>
                                    <span>Guardar Tema y Fondos</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

PlaylistThemeModal.propTypes = {
    isOpen: PropTypes.bool,
    settings: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    roomId: PropTypes.string
};

export default PlaylistThemeModal;
